/**
 * Folder Tree Component
 * Displays a hierarchical tree of folders with expand/collapse functionality
 */

import {
  html,
  attr,
  prop,
  Signal,
  Prop,
  When,
  Ensure,
  ForEach,
  on,
  untracked,
  OnDispose,
  type Renderable,
  Async,
} from '@tempots/dom'
import { Button, Icon, type ButtonVariant } from '@tempots/beatui'
import { ConfirmDialog, toast } from '@/components/ui'
import { createProjectFolders } from '@/services/gateway'
import type { FolderResponse } from '@/services/types'

// ============================================================================
// Types
// ============================================================================

export interface FolderTreeProps {
  /** Currently selected folder ID (empty string = root) */
  selectedFolderId: Signal<string>
  /** Callback when folder is selected */
  onSelect: (folderId: string) => void
  /** Callback when folder is created */
  onFolderCreated?: () => void
  /** Callback when folder is deleted */
  onFolderDeleted?: () => void
  /** Optional project ID - uses workspace state if not provided */
  projectId: Signal<string>
  /** Signal to trigger a refresh of folder data (increment to refresh) */
  refreshTrigger?: Signal<number>
}

interface FolderNode {
  folder: FolderResponse
  expanded: Prop<boolean>
  children: Prop<FolderNode[]>
  loading: Prop<boolean>
}

// ============================================================================
// Folder Tree Component
// ============================================================================

export function FolderTree(props: FolderTreeProps): Renderable {
  const {
    selectedFolderId,
    onSelect,
    onFolderCreated,
    onFolderDeleted,
    projectId,
    refreshTrigger,
  } = props

  const foldersService = projectId.map((pid) => {
    return createProjectFolders(pid)
  })

  // Root level folders
  const rootFolders = prop<FolderNode[]>([])

  // New folder creation state
  const creatingFolder = prop(false)
  const newFolderName = prop('')
  const createError = prop<string | null>(null)

  // Delete folder state
  const deletingFolder = prop(false)
  const deleteError = prop<string | null>(null)

  // Load root folders
  const loadRootFolders = async () => {
    const folders = foldersService.value
    if (!folders) {
      throw new Error('No project selected')
    }

    const response = await folders.list({ parentId: '', limit: 100 })
    /* eslint-disable tempots/require-async-signal-disposal -- using untracked() for disposal */
    rootFolders.set(
      response.folders.map((f) => ({
        folder: f,
        expanded: untracked(() => prop(false)),
        children: untracked(() => prop<FolderNode[]>([])),
        loading: untracked(() => prop(false)),
      }))
    )
    /* eslint-enable tempots/require-async-signal-disposal */
    return response.folders
  }

  // Load children for a folder
  const loadChildren = async (node: FolderNode) => {
    if (node.children.get().length > 0) {
      // Already loaded, just toggle
      node.expanded.set(!node.expanded.get())
      return
    }

    const folders = foldersService.value

    try {
      node.loading.set(true)
      const response = await folders.list({
        parentId: node.folder.id,
        limit: 100,
      })
      /* eslint-disable tempots/require-async-signal-disposal -- using untracked() for disposal */
      node.children.set(
        response.folders.map((f) => ({
          folder: f,
          expanded: untracked(() => prop(false)),
          children: untracked(() => prop<FolderNode[]>([])),
          loading: untracked(() => prop(false)),
        }))
      )
      /* eslint-enable tempots/require-async-signal-disposal */
      node.expanded.set(true)
    } catch {
      // Ignore errors for child loading
    } finally {
      node.loading.set(false)
    }
  }

  // Create new folder (supports nested paths like "foo/bar/baz")
  const handleCreateFolder = async () => {
    const name = newFolderName.get().trim()
    if (!name) return

    const folders = foldersService.value
    if (!folders) {
      createError.set('No project selected')
      return
    }

    try {
      createError.set(null)

      // Split path by "/" and filter out empty segments
      // eslint-disable-next-line tempots/require-async-signal-disposal
      const segments = name
        .split('/')
        .filter((segment) => segment.trim() !== '')

      if (segments.length === 0) {
        createError.set('Folder name cannot be empty')
        return
      }

      // Start from the currently selected folder (or root if none)
      let currentParentId = selectedFolderId.get() || undefined

      // Create each folder segment sequentially
      for (const segment of segments) {
        const created = await folders.create({
          name: segment,
          parentId: currentParentId,
        })
        // Use the newly created folder as the parent for the next segment
        currentParentId = created.id
      }

      newFolderName.set('')
      creatingFolder.set(false)
      await loadRootFolders()
      onFolderCreated?.()
    } catch (e) {
      createError.set(
        e instanceof Error ? e.message : 'Failed to create folder'
      )
    }
  }

  // Delete selected folder and all its contents (called from ConfirmDialog)
  const handleDeleteFolder = async () => {
    const folderId = selectedFolderId.get()
    if (!folderId) return

    const folders = foldersService.value
    if (!folders) {
      deleteError.set('No project selected')
      return
    }

    try {
      deletingFolder.set(true)
      deleteError.set(null)
      await folders.deleteRecursive(folderId)
      toast.success('Folder deleted')
      // Select root after deletion
      onSelect('')
      await loadRootFolders()
      onFolderDeleted?.()
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to delete folder'
      toast.error(message)
      deleteError.set(message)
    } finally {
      deletingFolder.set(false)
    }
  }

  // Get the current folder name for delete confirmation
  const getSelectedFolderName = () => {
    const folderId = selectedFolderId.get()
    if (!folderId) return 'this folder'
    return (
      rootFolders.get().find((n) => n.folder.id === folderId)?.folder.name ||
      'this folder'
    )
  }

  // Initial load promise
  const initialLoad = loadRootFolders()

  // Re-load when refresh trigger changes (e.g., after file upload)
  const unsubscribeRefresh = refreshTrigger?.on(() => {
    loadRootFolders()
  })

  return html.div(
    attr.class('flex flex-col h-full'),
    // Cleanup subscriptions on dispose
    OnDispose(() => unsubscribeRefresh?.()),
    // Header
    html.div(
      attr.class(
        'flex items-center justify-between px-3 py-2 border-b border-gray-200'
      ),
      html.h3(attr.class('text-sm font-semibold text-gray-700'), 'Folders'),
      html.div(
        attr.class('flex items-center gap-1'),
        // Delete folder button (only shown when a folder is selected)
        When(
          selectedFolderId.map((id) => id !== ''),
          () =>
            ConfirmDialog(
              {
                title: 'Delete Folder',
                message: `Delete "${getSelectedFolderName()}" and all its contents? This cannot be undone.`,
                confirmText: 'Delete',
                variant: 'danger',
                onConfirm: handleDeleteFolder,
              },
              Button(
                {
                  variant: 'ghost' as ButtonVariant,
                  size: 'sm',
                  disabled: deletingFolder,
                },
                When(
                  deletingFolder,
                  () =>
                    Icon(
                      { icon: 'mdi:loading' },
                      attr.class('w-4 h-4 animate-spin text-red-500')
                    ),
                  () =>
                    Icon(
                      { icon: 'mdi:folder-remove' },
                      attr.class('w-4 h-4 text-red-500')
                    )
                )
              )
            )
        ),
        Button(
          {
            variant: 'ghost' as ButtonVariant,
            size: 'sm',
            onClick: () => creatingFolder.set(true),
          },
          attr.title('Create new folder'),
          Icon({ icon: 'mdi:folder-plus' }, attr.class('w-4 h-4'))
        )
      )
    ),
    // Delete error message
    Ensure(deleteError, (err) =>
      html.div(attr.class('px-3 py-2 text-xs text-red-600 bg-red-50'), err)
    ),
    // New folder input
    When(creatingFolder, () =>
      html.div(
        attr.class('px-3 py-2 border-b border-gray-200 space-y-2'),
        html.input(
          attr.type('text'),
          attr.class(
            'w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
          ),
          attr.placeholder('Folder name'),
          attr.value(newFolderName),
          on.input((e) =>
            newFolderName.set((e.target as HTMLInputElement).value)
          ),
          on.keydown((e) => {
            if (e.key === 'Enter') handleCreateFolder()
            if (e.key === 'Escape') {
              creatingFolder.set(false)
              newFolderName.set('')
            }
          })
        ),
        Ensure(createError, (err) =>
          html.p(attr.class('text-xs text-red-600'), err)
        ),
        html.div(
          attr.class('flex gap-2'),
          Button(
            {
              size: 'sm',
              onClick: handleCreateFolder,
            },
            'Create'
          ),
          Button(
            {
              variant: 'ghost' as ButtonVariant,
              size: 'sm',
              onClick: () => {
                creatingFolder.set(false)
                newFolderName.set('')
                createError.set(null)
              },
            },
            'Cancel'
          )
        )
      )
    ),
    // Folder list
    html.div(
      attr.class('flex-1 overflow-y-auto py-1'),
      // Root/All Files item
      html.div(
        attr.class(
          selectedFolderId.map((id) =>
            id === ''
              ? 'flex items-center gap-2 px-3 py-1.5 cursor-pointer bg-brand-50 text-brand-700'
              : 'flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-100 text-gray-700'
          )
        ),
        on.click(() => onSelect('')),
        Icon({ icon: 'mdi:folder-home' }, attr.class('w-4 h-4')),
        html.span(attr.class('text-sm font-medium'), 'All Files')
      ),
      // Async load root folders
      Async(initialLoad, {
        pending: () =>
          html.div(
            attr.class('px-3 py-4 text-center'),
            Icon(
              { icon: 'mdi:loading' },
              attr.class('w-5 h-5 animate-spin text-gray-400 mx-auto')
            )
          ),
        error: (err) =>
          html.div(
            attr.class('px-3 py-2 text-xs text-red-600'),
            err instanceof Error ? err.message : 'Failed to load folders'
          ),
        then: () =>
          ForEach(rootFolders, (nodeSignal) =>
            FolderTreeItem({
              nodeSignal,
              depth: 0,
              selectedFolderId,
              onSelect,
              onToggle: loadChildren,
            })
          ),
      })
    )
  )
}

// ============================================================================
// Folder Tree Item Component
// ============================================================================

interface FolderTreeItemProps {
  nodeSignal: Signal<FolderNode>
  depth: number
  selectedFolderId: Signal<string>
  onSelect: (folderId: string) => void
  onToggle: (node: FolderNode) => void
}

function FolderTreeItem(props: FolderTreeItemProps): Renderable {
  const { nodeSignal, depth, selectedFolderId, onSelect, onToggle } = props
  const node = nodeSignal.get()
  const hasChildren = node.folder.folderCount > 0
  const paddingLeft = 12 + depth * 16

  return html.div(
    // Folder row
    html.div(
      attr.class(
        selectedFolderId.map((id) =>
          id === node.folder.id
            ? 'flex items-center gap-1 py-1.5 cursor-pointer bg-brand-50 text-brand-700'
            : 'flex items-center gap-1 py-1.5 cursor-pointer hover:bg-gray-100 text-gray-700'
        )
      ),
      attr.style(`padding-left: ${paddingLeft}px`),
      // Expand/collapse button
      html.div(
        attr.class('w-5 h-5 flex items-center justify-center'),
        hasChildren
          ? html.button(
              attr.class('hover:bg-gray-200 rounded'),
              on.click((e) => {
                e.stopPropagation()
                onToggle(node)
              }),
              When(
                node.loading,
                () =>
                  Icon(
                    { icon: 'mdi:loading' },
                    attr.class('w-4 h-4 animate-spin')
                  ),
                () =>
                  Icon(
                    {
                      icon: node.expanded.map((exp): string =>
                        exp ? 'mdi:chevron-down' : 'mdi:chevron-right'
                      ),
                    },
                    attr.class('w-4 h-4')
                  )
              )
            )
          : null
      ),
      // Folder icon and name
      html.div(
        attr.class('flex items-center gap-2 flex-1 min-w-0'),
        on.click(() => onSelect(node.folder.id)),
        Icon(
          {
            icon: node.expanded.map((exp): string =>
              exp ? 'mdi:folder-open' : 'mdi:folder'
            ),
          },
          attr.class('w-4 h-4 flex-shrink-0')
        ),
        html.span(attr.class('text-sm truncate'), node.folder.name)
      )
    ),
    // Children
    When(node.expanded, () =>
      ForEach(node.children, (childNodeSignal) =>
        FolderTreeItem({
          nodeSignal: childNodeSignal,
          depth: depth + 1,
          selectedFolderId,
          onSelect,
          onToggle,
        })
      )
    )
  )
}
